import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    LocationOn as LocationOnIcon,
    Home as HomeIcon,
    DirectionsTransit as DirectionsTransitIcon,
    CheckCircle as CheckCircleIcon,
    ListAlt as ListAltIcon,
    MeetingRoom as MeetingRoomIcon,
    AccessTime as AccessTimeIcon,
    ArrowRightAlt as ArrowRightAltIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    {
        id: 'introduction',
        label: '立地と広さのバランスを考える',
    },
    {
        id: 'ekichika-merit',
        label: '駅近物件のメリット',
    },
    {
        id: 'hirosa-merit',
        label: '広い物件のメリット',
    },
    {
        id: 'commute',
        label: '通勤・通学時間が生活に与える影響',
    },
    {
        id: 'priority-checklist',
        label: '優先順位チェックリスト＆アドバイス',
    },
    {
        id: 'conclusion',
        label: 'まとめ：あなたに最適なルームシェア物件選びのヒント',
    },
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            bgcolor: '#f5f5f5',
                                            color: '#888',
                                            fontWeight: 'bold',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 1,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.95rem', textAlign: 'left', p: 0, lineHeight: 1.3 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入部分 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    立地と広さのバランスを考える
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア物件を探すとき、多くの人が「駅近」と「広さ」のどちらを優先すべきか悩みます。通勤・通学の便利さを取るか、伸びやかな生活空間を取るか――それぞれに大きなメリットがあり、どちらかを選ぶことで日々の満足度やストレスレベルが大きく変わることも。この記事では、立地と広さ、そのバランスの取り方を徹底解説。あなたに合ったルームシェア物件選びのヒントと、条件整理のためのチェックリストもご紹介します。
                </Typography>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    
                </Box>
                <Typography variant="body2" color="text.secondary">
                    立地と広さ、それぞれの条件があなたの生活スタイルにどう影響するのか、専門家の視点でわかりやすく解説します。
                </Typography>
            </Box>

            {/* 駅近物件のメリット */}
            <Box id="ekichika-merit" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <LocationOnIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    駅近物件のメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            駅徒歩5分以内の物件は、毎日の生活を効率的かつ快適にしてくれます。特に都心部や交通網が発達したエリアでは、「駅近」であること自体が大きな価値となり、生活の“質”を大きく左右します。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <DirectionsTransitIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">通勤・通学が圧倒的にラク</Typography>}
                                    secondary="朝の準備や帰宅後の時間に余裕ができ、毎日がストレスフリーに。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <AccessTimeIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">時間の節約</Typography>}
                                    secondary="移動時間が最小限なので、趣味や自己投資、ルームメイトとの交流時間も充実。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <HomeIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">生活利便性の高さ</Typography>}
                                    secondary="コンビニやスーパー、カフェ、ジムなどが近く、日常の買い物や外食も便利。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <MeetingRoomIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">ルームシェア仲間が集まりやすい</Typography>}
                                    secondary="友人や新しいルームメイトとの待ち合わせも簡単。"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" color="text.secondary">
                            <ArrowRightAltIcon sx={{ verticalAlign: 'middle' }} /> 駅近物件は家賃が高めになることも。一方で、日々の「時短」と「利便性」がもたらす価値は計り知れません。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box>
                            <img
                                src="/images/blog/ekichika-building_ekichika-vs-hirosa-roomshare-priority.jpg"
                                alt="駅から徒歩2分の場所にある、近代的な外観のマンション。周辺にはコンビニやカフェ、バス停などもあり、都会的で利便性の高い生活環境が伝わる写真。"
                                style={{ width: '100%', maxWidth: 220, borderRadius: 10, boxShadow: '0 2px 8px #bbb' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 広い物件のメリット */}
            <Box id="hirosa-merit" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <HomeIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                    広い物件のメリット
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            「リビングが広い」「個室が広い」など、物件の広さはルームシェアの暮らしやすさに直結します。駅から少し離れることで、同じ家賃でもより広い空間を手に入れることが可能です。
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <HomeIcon color="secondary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">プライベート空間の充実</Typography>}
                                    secondary="自分だけの個室や作業スペースが確保でき、ストレスが溜まりにくい。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <MeetingRoomIcon color="secondary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">共有スペースでの交流が活発に</Typography>}
                                    secondary="広いリビングやダイニングでルームメイト同士の交流も自然と増える。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="secondary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">コストパフォーマンスが高い</Typography>}
                                    secondary="同じ家賃帯でも、駅から少し離れるだけでゆとりある空間を確保できる。"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <ListAltIcon color="secondary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={<Typography variant="subtitle1" fontWeight="bold">趣味や在宅ワークにも最適</Typography>}
                                    secondary="趣味スペースやリモートワーク用デスクも置ける広さは、現代のライフスタイルにフィット。"
                                />
                            </ListItem>
                        </List>
                        <Typography variant="body2" color="text.secondary">
                            <ArrowRightAltIcon sx={{ verticalAlign: 'middle', color: 'secondary.main' }} /> 「広さ優先」の場合は周辺環境や交通の便、夜道の安全も忘れずにチェックしましょう。
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box>
                            
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* 通勤・通学時間が生活に与える影響 */}
            <Box id="commute" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <DirectionsTransitIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    通勤・通学時間が生活に与える影響
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    物件探しで「立地」と「広さ」のバランスを考えるうえで最も重要なのは、毎日の通勤・通学時間です。
                </Typography>
                <Box sx={{ mb: 3 }}>
                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            通勤・通学時間が長いと…
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <AccessTimeIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="平日の自由時間が減り、趣味やリラックスの時間が取りにくい"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <AccessTimeIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="疲労やストレスが蓄積しやすく、体調不良にもつながる"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <AccessTimeIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="ルームメイトとの交流の時間が減る"
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Box>
                <Box>
                    <Paper elevation={2} sx={{ p: 2, bgcolor: '#f8fafe', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                            一方、やや遠い物件でも…
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    <HomeIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="広く快適な自宅でリフレッシュできる"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <MeetingRoomIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="在宅ワークや趣味の時間を充実させられる"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="通勤ラッシュを避けられる場合も"
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    <DoneAllIcon sx={{ verticalAlign: 'middle', color: 'primary.light' }} /> あなたが「時間重視」か「空間重視」かを明確にし、その上で適切なバランスを見極めることが大切です。
                </Typography>
            </Box>

            {/* 優先順位チェックリスト＆アドバイス */}
            <Box id="priority-checklist" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <ListAltIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                    優先順位チェックリスト＆アドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「自分にとって何が大切か？」を整理するためのチェックリストです。ルームメイトと一緒に話し合いながら使うのもおすすめです。
                </Typography>
                <Box sx={{ mb: 3 }}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5faff', borderRadius: 2 }}>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="毎日の通勤・通学時間は何分まで許容できる？"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="リビングや個室の広さ、収納スペースはどれくらい必要？"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="物件の周辺環境（スーパー・カフェ・治安など）は重視する？"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="家賃・光熱費などのトータルコストは？"
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="将来的にリモートワークや趣味のための空間が必要？"
                                />
                            </ListItem>
                        </List>
                    </Paper>
                </Box>
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        専門家からのアドバイス
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        ルームシェアでの物件探しは、自分一人だけの希望でなく、全員のライフスタイルや価値観をすり合わせることが成功のカギです。
                    </Typography>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="譲れない条件と妥協できる条件をリスト化して話し合う"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="「駅近」「広さ」の両立が難しい場合、通勤ルートや交通手段の選択肢も検討"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <CheckCircleIcon color="success" />
                            </ListItemIcon>
                            <ListItemText
                                primary="実際に内見し、周辺環境や騒音も必ずチェック"
                            />
                        </ListItem>
                    </List>
                </Box>
                <Box sx={{ my: 3, textAlign: 'center' }}>
                    
                </Box>
            </Box>

            {/* まとめ */}
            <Box id="conclusion" sx={{ mb: 7 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    <DoneAllIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'secondary.main' }} />
                    まとめ：あなたに最適なルームシェア物件選びのヒント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアの物件選びは、「駅近」か「広さ」かを単純に決めるのではなく、<b>自分たちのライフスタイル・価値観・将来設計</b>も踏まえて総合的に判断することが重要です。
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Chip label="駅近重視派" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="広さ重視派" color="secondary" variant="outlined" sx={{ mr: 1 }} />
                    <Chip label="バランス派" color="success" variant="outlined" />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    どちらかを選んでも、完璧な物件はなかなかありません。<b>優先順位を明確にし、妥協点を見つけるプロセス</b>自体が、より良いルームシェア生活の第一歩です。
                </Typography>
                <Typography variant="body1">
                    <b>まずは条件を書き出し、内見を重ねて実際の暮らしをシミュレーションしてみましょう。</b>あなたの理想のルームシェアライフが、きっと見つかるはずです。
                </Typography>
            </Box>
        </Box>
    );
};

const post = {
    id: 'ekichika-vs-hirosa-roomshare-priority',
    slug: 'ekichika-vs-hirosa-roomshare-priority',
    title: '駅近vs広さ：ルームシェアで重視すべき物件条件の優先順位',
    excerpt: 'ルームシェア物件を選ぶ際に悩みがちな「駅近」と「広さ」。それぞれのメリットやデメリット、通勤・通学時間の影響、優先順位の付け方まで専門的に解説します。立地と空間のどちらを重視するか迷う方に、具体的なチェックリストとアドバイスを提供します。',
    coverImage: '/images/blog/cover-ekichika-vs-hirosa_ekichika-vs-hirosa-roomshare-priority.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。開放的な空間と協調性の雰囲気が伝わるシーン。',
    publishedAt: '2024-06-22T09:00:00+09:00',
    isPublished: true,
    categories: ['ルームシェア', '物件選び', '暮らしのコツ'],
    tags: ['駅近', '広さ', 'ルームシェア', '優先順位', '通勤・通学'],
    author: {
        name: '村上 直人',
        avatar: '/images/avatars/naoto-murakami.jpg',
        bio: 'シェアハウス運営歴10年以上。物件選びやコミュニティ形成のアドバイザーとして、多様なライフスタイルに合ったルームシェアのノウハウを発信中。',
        profilePicture: '/images/avatars/naoto-murakami.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;